// /portal/compliance — the client-facing KYC action surface (portal-kyc Phase D).
//
// Data sources (PLAN §6a / portal_ux load_sequence):
//   GET /api/portal/kyc/status        → rollup + per-category state + file cap
//   GET /api/portal/kyc/requirements  → checklist (entity_type + country driven)
//   GET /api/portal/kyc/documents     → own KYC docs (for uploaded_by / detail)
//   GET /api/clients/:id              → entity country (cross-border conditional)
//
// Consent gate: any gated call returning 412 `kyc_consent_required` opens the
// consent modal. On accept we POST /consent, then retry. The re-prompt loop is
// bounded (CONSENT_RETRY_CAP) so a persistent version mismatch can't spin.
//
// REGULATORY COPY DISCIPLINE (REQ-008): every string of regulatory weight on
// this page is read via useLegal() through the four cmsLegal slots authored in
// Phase F (kyc_processing_notice / kyc_consent_v1 / data_retention_policy /
// cross_border_transfer_notice). Those slots may be ABSENT until Phase F — we
// render a neutral placeholder or hide the block rather than crash, and never
// hardcode the prose. Operational strings (chip labels, button text, progress
// + error messages) are NOT regulatory and are fine to hardcode.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getKycStatusLabel,
  type DocumentDto,
  type KycCategoryConfig,
  type KycReviewStatus,
  type KycStatus,
} from '@shared';
import { apiGet, apiPost, apiDelete, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { useLegal } from '../../lib/legal-context';
import { ComplianceUpload } from './ComplianceUpload';

// ── Wire shapes (match src/server/routes/portal-kyc.ts) ─────────────────────

type CategoryState = KycReviewStatus | 'missing';

interface ByCategoryEntry {
  state: CategoryState;
  document_id?: string;
  rejected_reason?: string | null;
  retention_until?: string | null;
}

interface StatusResponse {
  kyc: KycStatus;
  by_category: Record<string, ByCategoryEntry>;
  required: string[];
  files_used: number;
  files_cap: number;
  consent_current: boolean;
}

// Countries that trigger the cross-border transfer notice (PLAN §8 /
// acceptance criterion 17). UK + EU do NOT trigger it.
const CROSS_BORDER_COUNTRIES = new Set([
  'AE-MAIN', 'AE-FZ', 'IN', 'SG', 'BVI', 'KY', 'SEA', 'NA', 'OTHER',
]);

// cmsLegal slot keys authored in Phase F. Read defensively — the slots are not
// part of the typed CmsLegal shape yet, so we widen to a string map.
const SLOT_PROCESSING = 'kyc_processing_notice';
const SLOT_CONSENT = 'kyc_consent_v1';
const SLOT_RETENTION = 'data_retention_policy';
const SLOT_CROSS_BORDER = 'cross_border_transfer_notice';

// Placeholder doc id + version used for the consent record while the cmsLegal
// slot is unauthored. Phase B's server treats version 1 as current; Phase F
// replaces this with the real cmsLegal pointer.
const CONSENT_DOC_ID_FALLBACK = SLOT_CONSENT;
const CONSENT_VERSION_FALLBACK = 1;

const CONSENT_RETRY_CAP = 2;

// Operational chip tone per category state (support-cx precedent — inline chip,
// not StatusChip). These are UI labels, not regulatory copy.
function chipToneForState(s: CategoryState): string {
  switch (s) {
    case 'accepted': return 'green';
    case 'in_review': return 'accent';
    case 'rejected': return 'red';
    case 'uploaded': return 'accent';
    case 'superseded': return 'neutral';
    case 'missing': default: return 'neutral';
  }
}

function stateLabel(s: CategoryState): string {
  switch (s) {
    case 'missing': return 'Not provided';
    case 'uploaded': return 'Ready to submit';
    case 'in_review': return 'Under review';
    case 'accepted': return 'Accepted';
    case 'rejected': return 'Action needed';
    case 'superseded': return 'Replaced';
    default: return s;
  }
}

function rollupChipTone(s: KycStatus): string {
  switch (s) {
    case 'current': return 'green';
    case 'in_review': return 'accent';
    case 'rejected': return 'red';
    case 'expired': return 'red';
    case 'refresh': return 'amber';
    case 'pending': default: return 'neutral';
  }
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Consent modal ───────────────────────────────────────────────────────────

interface ConsentModalProps {
  processingNotice?: string;
  consentText?: string;
  crossBorderNotice?: string;
  showCrossBorder: boolean;
  onAccept: (acceptedCrossBorder: boolean) => void;
  onDecline: () => void;
  submitting: boolean;
  error: string | null;
}

function ConsentModal(props: ConsentModalProps) {
  const [agreed, setAgreed] = useState(false);
  const [agreedCrossBorder, setAgreedCrossBorder] = useState(false);
  const canAccept = agreed && (!props.showCrossBorder || agreedCrossBorder);

  // z-index above the upload modal (.kyc-modal-backdrop = 80): the consent gate
  // fires while the upload modal is still mounted, so the notice must sit on top
  // or the user can't accept it and is dead-ended.
  return (
    <div className="kyc-modal-backdrop" style={{ zIndex: 90 }} role="dialog" aria-modal="true" aria-label="KYC processing notice">
      <div className="kyc-modal card kyc-modal--tall">
        <h3 style={{ fontSize: 18, margin: 0 }}>Before you continue</h3>

        {/* Regulatory prose — sourced from cmsLegal (REQ-008). When unauthored,
            show a neutral placeholder rather than hardcoding the notice. */}
        <div className="kyc-legal-body" style={{ marginTop: 14 }}>
          {props.processingNotice
            ? props.processingNotice
            : 'Our data-processing notice will be shown here. Please contact your engagement lead before uploading documentation if this section is blank.'}
        </div>

        {props.showCrossBorder && (
          <div className="kyc-legal-body" style={{ marginTop: 14 }}>
            {props.crossBorderNotice
              ? props.crossBorderNotice
              : 'A cross-border transfer notice applies to your jurisdiction and will be shown here once published.'}
          </div>
        )}

        <label
          className={`checkbox ${agreed ? 'on' : ''}`}
          onClick={() => setAgreed((v) => !v)}
          style={{ marginTop: 18 }}
        >
          <span className="box" />
          <span>{props.consentText || 'I have read and agree to the above.'}</span>
        </label>

        {props.showCrossBorder && (
          <label
            className={`checkbox ${agreedCrossBorder ? 'on' : ''}`}
            onClick={() => setAgreedCrossBorder((v) => !v)}
            style={{ marginTop: 10 }}
          >
            <span className="box" />
            <span>I consent to the cross-border transfer of my data as described above.</span>
          </label>
        )}

        {props.error && (
          <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 14 }}>{props.error}</div>
        )}

        <div className="kyc-modal-actions">
          <button type="button" className="btn ghost" onClick={props.onDecline} disabled={props.submitting}>
            Not now
          </button>
          <button
            type="button"
            className="btn accent"
            onClick={() => props.onAccept(props.showCrossBorder && agreedCrossBorder)}
            disabled={!canAccept || props.submitting}
          >
            {props.submitting ? 'Recording…' : 'Agree & continue →'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Submit confirmation modal ────────────────────────────────────────────────

function SubmitConfirm({ count, onConfirm, onCancel, busy }: {
  count: number; onConfirm: () => void; onCancel: () => void; busy: boolean;
}) {
  return (
    <div className="kyc-modal-backdrop" role="dialog" aria-modal="true" aria-label="Confirm submission">
      <div className="kyc-modal card">
        <h3 style={{ fontSize: 17, margin: 0 }}>Submit for review</h3>
        <p style={{ fontSize: 13.5, color: 'var(--fg-soft)', marginTop: 12, lineHeight: 1.55 }}>
          You are about to submit {count} document{count === 1 ? '' : 's'} for review. After
          submission you can no longer delete or replace {count === 1 ? 'it' : 'them'} until
          our compliance team responds.
        </p>
        <div className="kyc-modal-actions">
          <button type="button" className="btn ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="btn accent" onClick={onConfirm} disabled={busy}>
            {busy ? 'Submitting…' : `Submit ${count} →`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function PortalCompliance() {
  const { user } = useAuth();
  const legal = useLegal();
  // The four KYC slots are not part of the typed CmsLegal shape (they land in
  // Phase F). Read them through a widened view; undefined when unauthored.
  const slots = legal as unknown as Record<string, string | undefined>;
  const processingNotice = slots[SLOT_PROCESSING];
  const consentText = slots[SLOT_CONSENT];
  const retentionPolicy = slots[SLOT_RETENTION];
  const crossBorderNotice = slots[SLOT_CROSS_BORDER];

  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [requirements, setRequirements] = useState<KycCategoryConfig[]>([]);
  const [documents, setDocuments] = useState<DocumentDto[]>([]);
  const [country, setCountry] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Consent gate
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentSubmitting, setConsentSubmitting] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [consentRetries, setConsentRetries] = useState(0);

  // Upload modal
  const [uploadFor, setUploadFor] = useState<{ category: KycCategoryConfig; mode: 'upload' | 'replace' } | null>(null);

  // Submit
  const [submitConfirm, setSubmitConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const showCrossBorder = country !== null && CROSS_BORDER_COUNTRIES.has(country);

  const loadStatus = useCallback(async () => {
    const [s, d] = await Promise.all([
      apiGet<StatusResponse>('/api/portal/kyc/status'),
      apiGet<{ documents: DocumentDto[] }>('/api/portal/kyc/documents'),
    ]);
    setStatus(s);
    setDocuments(d.documents);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [s, req, d] = await Promise.all([
          apiGet<StatusResponse>('/api/portal/kyc/status'),
          apiGet<{ categories: KycCategoryConfig[] }>('/api/portal/kyc/requirements'),
          apiGet<{ documents: DocumentDto[] }>('/api/portal/kyc/documents'),
        ]);
        if (cancelled) return;
        setStatus(s);
        setRequirements(req.categories);
        setDocuments(d.documents);
      } catch (err) {
        if (cancelled) return;
        // status/documents/requirements are not consent-gated, so a 412 here is
        // unexpected; surface a friendly error.
        setError(err instanceof ApiError ? err.serverMessage : 'Could not load your compliance status.');
      } finally {
        if (!cancelled) setLoading(false);
      }
      // Client profile for the cross-border conditional — soft-fails.
      if (user?.client_id) {
        try {
          const c = await apiGet<{ client: { country: string } }>(`/api/clients/${user.client_id}`);
          if (!cancelled) setCountry(c.client.country);
        } catch { /* leave country null — cross-border block simply hides */ }
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [user?.client_id]);

  // Consent gate: opened when the upload modal's /upload-url call (or a submit)
  // returns 412. Bounded so a persistent version mismatch can't loop.
  function requestConsent() {
    if (consentRetries >= CONSENT_RETRY_CAP) {
      setActionError('We could not record your consent. Please contact your engagement lead.');
      return;
    }
    setConsentError(null);
    setConsentOpen(true);
  }

  async function acceptConsent(acceptedCrossBorder: boolean) {
    setConsentSubmitting(true);
    setConsentError(null);
    try {
      await apiPost('/api/portal/kyc/consent', {
        kind: 'kyc_processing',
        consent_text_doc_id: CONSENT_DOC_ID_FALLBACK,
        consent_text_version: CONSENT_VERSION_FALLBACK,
      });
      if (acceptedCrossBorder) {
        await apiPost('/api/portal/kyc/consent', {
          kind: 'cross_border_transfer',
          consent_text_doc_id: SLOT_CROSS_BORDER,
          consent_text_version: CONSENT_VERSION_FALLBACK,
        });
      }
      setConsentOpen(false);
      setConsentRetries((n) => n + 1);
      // Refresh status so consent_current flips true and the upfront banner
      // clears. The upload modal (if open) stays mounted for retry.
      void loadStatus();
    } catch (err) {
      setConsentError(err instanceof ApiError ? err.serverMessage : 'Could not record consent.');
    } finally {
      setConsentSubmitting(false);
    }
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  function openUpload(category: KycCategoryConfig, mode: 'upload' | 'replace') {
    setActionError(null);
    setUploadFor({ category, mode });
  }

  function onUploaded(doc: DocumentDto) {
    setUploadFor(null);
    setActionError(null);
    // Optimistic-ish: re-fetch authoritative status + docs.
    void loadStatus();
    setDocuments((prev) => [doc, ...prev.filter((d) => d.id !== doc.id)]);
  }

  async function deleteDoc(documentId: string) {
    setActionError(null);
    try {
      await apiDelete(`/api/portal/kyc/documents/${documentId}`);
      await loadStatus();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.serverMessage : 'Could not delete the document.');
    }
  }

  async function submitAll() {
    setSubmitting(true);
    setActionError(null);
    try {
      await apiPost<{ flipped: number }>('/api/portal/kyc/submit', {});
      await loadStatus();
      setSubmitConfirm(false);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.serverMessage : 'Could not submit for review.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Derived ────────────────────────────────────────────────────────────────

  const byCategory = status?.by_category ?? {};
  const docById = useMemo(() => {
    const m: Record<string, DocumentDto> = {};
    for (const d of documents) m[d.id] = d;
    return m;
  }, [documents]);

  const uploadedCount = useMemo(
    () => Object.values(byCategory).filter((e) => e.state === 'uploaded').length,
    [byCategory],
  );
  const acceptedCount = useMemo(
    () => Object.values(byCategory).filter((e) => e.state === 'accepted').length,
    [byCategory],
  );
  const requiredCount = status?.required.length ?? requirements.length;

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <div>
        <h1 className="portal-h1">KYC &amp; Compliance</h1>
        <p className="portal-sub">
          Upload the documentation we need to verify your company. Each item is reviewed by our
          compliance team before your account is cleared for engagements.
        </p>
      </div>

      {!loading && status && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
          <span className={`chip ${rollupChipTone(status.kyc)}`}>{getKycStatusLabel(status.kyc, 'client')}</span>
          <span style={{ fontSize: 13, color: 'var(--fg-soft)' }}>
            {acceptedCount} of {requiredCount} required document{requiredCount === 1 ? '' : 's'} accepted
          </span>
          <span style={{ fontSize: 12.5, color: 'var(--fg-mute)' }}>
            · {status.files_used} of {status.files_cap} files used
          </span>
        </div>
      )}

      {error && (
        <div className="card" style={{ marginTop: 16, borderColor: 'var(--red)', color: 'var(--red)' }}>
          {error}
        </div>
      )}
      {actionError && (
        <div className="card" style={{ marginTop: 16, borderColor: 'var(--red)', color: 'var(--red)' }}>
          {actionError}
        </div>
      )}

      {loading && (
        <div className="card" style={{ marginTop: 20, color: 'var(--fg-mute)' }}>Loading your checklist…</div>
      )}

      {/* One-time consent prompt (non-blocking). Shown until THIS user has
          accepted the data-processing notice; decoupled from any single
          document — accepting here ungates all uploads. */}
      {!loading && status && !status.consent_current && (
        <div
          className="card"
          style={{
            marginTop: 16, display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', gap: 16, flexWrap: 'wrap',
            borderColor: 'var(--accent-line)', background: 'var(--accent-soft)',
          }}
        >
          <div>
            <div style={{ fontWeight: 500, fontSize: 14 }}>Before you upload</div>
            <div style={{ fontSize: 12.5, color: 'var(--fg-soft)', marginTop: 2, lineHeight: 1.5 }}>
              Please review and accept our data-processing notice. You only need to do this once —
              then you can upload any of the documents below.
            </div>
          </div>
          <button type="button" className="btn sm accent" onClick={requestConsent}>
            Review &amp; accept
          </button>
        </div>
      )}

      {/* Checklist */}
      {!loading && status && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 20 }}>
          {requirements.map((cat) => {
            const entry = byCategory[cat.key] ?? { state: 'missing' as CategoryState };
            const doc = entry.document_id ? docById[entry.document_id] : undefined;
            const canDelete =
              entry.state === 'uploaded' &&
              doc != null &&
              doc.uploaded_by_user_id === user?.id;

            return (
              <article key={cat.key} className="card kyc-cat-card">
                <div className="kyc-cat-main">
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 14.5 }}>{cat.label}</div>
                    {cat.description && (
                      <div style={{ fontSize: 12.5, color: 'var(--fg-mute)', marginTop: 4, lineHeight: 1.5 }}>
                        {cat.description}
                      </div>
                    )}
                  </div>
                  <span className={`chip ${chipToneForState(entry.state)}`} style={{ flexShrink: 0 }}>
                    {stateLabel(entry.state)}
                  </span>
                </div>

                {/* Rejected — show reason inline + replace */}
                {entry.state === 'rejected' && entry.rejected_reason && (
                  <div style={{ marginTop: 10, padding: 12, borderRadius: 'var(--r-sm)', background: 'rgba(184, 68, 58, 0.08)', color: 'var(--red)', fontSize: 12.5, lineHeight: 1.55 }}>
                    {entry.rejected_reason}
                  </div>
                )}

                {/* Accepted — read-only retention line */}
                {entry.state === 'accepted' && (
                  <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--fg-soft)' }}>
                    Accepted{entry.retention_until ? ` · retained until ${formatDate(entry.retention_until)}` : ''}
                  </div>
                )}

                {/* in_review — locked */}
                {entry.state === 'in_review' && (
                  <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--fg-soft)' }}>
                    Review in progress — you'll be notified when there's an update.
                  </div>
                )}

                <div className="kyc-cat-actions">
                  {(entry.state === 'missing') && (
                    <button type="button" className="btn sm accent" onClick={() => openUpload(cat, 'upload')}>
                      Upload
                    </button>
                  )}
                  {entry.state === 'rejected' && (
                    <button type="button" className="btn sm accent" onClick={() => openUpload(cat, 'replace')}>
                      Upload replacement
                    </button>
                  )}
                  {entry.state === 'uploaded' && (
                    <>
                      <button type="button" className="btn sm" onClick={() => openUpload(cat, 'replace')}>
                        Replace
                      </button>
                      {canDelete && (
                        <button type="button" className="btn sm ghost" onClick={() => entry.document_id && deleteDoc(entry.document_id)}>
                          Delete
                        </button>
                      )}
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Submit panel */}
      {!loading && status && (
        <div className="card" style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 500, fontSize: 14 }}>Ready to submit</div>
            <div style={{ fontSize: 12.5, color: 'var(--fg-soft)', marginTop: 2 }}>
              {uploadedCount === 0
                ? 'Upload at least one document before submitting.'
                : `${uploadedCount} document${uploadedCount === 1 ? '' : 's'} ready for review.`}
            </div>
          </div>
          <button
            type="button"
            className="btn accent"
            disabled={uploadedCount === 0}
            onClick={() => setSubmitConfirm(true)}
          >
            Submit for review →
          </button>
        </div>
      )}

      {/* Footer — retention link + DPO + rights + last consent. Regulatory copy
          via useLegal(); hidden when the slot is unauthored. */}
      {!loading && (
        <footer className="kyc-footer">
          {retentionPolicy && (
            <details className="kyc-footer-item">
              <summary>Data retention</summary>
              <div className="kyc-legal-body" style={{ marginTop: 8 }}>{retentionPolicy}</div>
            </details>
          )}
          {processingNotice && (
            <details className="kyc-footer-item">
              <summary>Your rights &amp; how we use your data</summary>
              <div className="kyc-legal-body" style={{ marginTop: 8 }}>{processingNotice}</div>
            </details>
          )}
          <div className="kyc-footer-item">
            <a href="mailto:dpo@omegatrade.co.uk">Contact our Data Protection Officer</a>
          </div>
        </footer>
      )}

      {/* Modals */}
      {consentOpen && (
        <ConsentModal
          processingNotice={processingNotice}
          consentText={consentText}
          crossBorderNotice={crossBorderNotice}
          showCrossBorder={showCrossBorder}
          onAccept={acceptConsent}
          onDecline={() => setConsentOpen(false)}
          submitting={consentSubmitting}
          error={consentError}
        />
      )}

      {uploadFor && (
        <ComplianceUpload
          category={uploadFor.category}
          mode={uploadFor.mode}
          onClose={() => setUploadFor(null)}
          onUploaded={onUploaded}
          onConsentRequired={requestConsent}
        />
      )}

      {submitConfirm && (
        <SubmitConfirm
          count={uploadedCount}
          onConfirm={submitAll}
          onCancel={() => setSubmitConfirm(false)}
          busy={submitting}
        />
      )}
    </>
  );
}
